/**
 * Fields: the user-facing view of label groups. An exclusive group is a single select, any other
 * group a multi select; ungrouped labels stay plain tags. Pure helpers shared by the web app, MCP
 * and the query resolver. Browser-safe: no database imports (`@plannello/core/fields`).
 *
 * The schema arguments are structural, so both `ProductSchema` (from `getSchema`) and
 * `QuerySchema` (query resolution) can be passed as they are.
 */
import type { FieldKind, Localized } from "@plannello/shared";

export type FieldScope = "instance" | "space" | "product";

export interface FieldGroupDef {
  id: string;
  slug: string;
  name: Localized;
  exclusive: boolean;
  /** Empty = all item types. */
  appliesToTypeIds: string[];
  /** Item types that must carry at least one label of the group. Empty = none. */
  requiredForTypeIds: string[];
  position: number;
  /** Omitted = instance scope (applies everywhere). */
  scope?: FieldScope;
  scopeId?: string | null;
  archivedAt?: unknown;
}

export interface FieldLabelDef {
  id: string;
  slug: string;
  name: Localized;
  color?: string;
  groupId: string | null;
  position: number;
  scope?: FieldScope;
  scopeId?: string | null;
  archivedAt?: unknown;
}

export interface FieldsSchema {
  labelGroups: readonly FieldGroupDef[];
  labels: readonly FieldLabelDef[];
  /** `getSchema` shape: the one product the schema was loaded for. */
  product?: { id: string; spaceId: string };
  /** Query-schema shape: every visible product. */
  products?: readonly { id: string; spaceId: string }[];
}

export interface FieldLabel {
  id: string;
  slug: string;
  name: Localized;
  color: string | undefined;
  position: number;
}

export interface Field {
  /** Label group id. */
  id: string;
  /** Label group slug (also usable as a query key: `area:auth`). */
  slug: string;
  name: Localized;
  kind: FieldKind;
  /** The item's type requires at least one value. */
  required: boolean;
  /** Selectable labels, in position order. */
  labels: FieldLabel[];
}

type ScopeRow = { scope?: FieldScope | undefined; scopeId?: string | null | undefined };

/** Whether a scoped row (group or label) applies to the product. Without product info only instance rows do. */
function scopeApplies(row: ScopeRow, product: { id: string; spaceId: string } | undefined): boolean {
  const scope = row.scope ?? "instance";
  if (scope === "instance") return true;
  if (!product) return false;
  return scope === "space" ? row.scopeId === product.spaceId : row.scopeId === product.id;
}

function findProduct(schema: FieldsSchema, productId: string): { id: string; spaceId: string } | undefined {
  if (schema.product?.id === productId) return schema.product;
  return schema.products?.find((p) => p.id === productId);
}

export const fieldKind = (group: Pick<FieldGroupDef, "exclusive">): FieldKind => (group.exclusive ? "single" : "multi");

/**
 * The Fields that apply to an item of `typeId` in `productId`: groups that are not archived, apply
 * to the product's scope and to the type, ordered by position (then slug). Each carries its
 * selectable labels (not archived, in the product's scope).
 */
export function fieldsForItem(schema: FieldsSchema, typeId: string, productId: string): Field[] {
  const product = findProduct(schema, productId);
  const groups = schema.labelGroups
    .filter(
      (g) =>
        !g.archivedAt &&
        scopeApplies(g, product) &&
        (g.appliesToTypeIds.length === 0 || g.appliesToTypeIds.includes(typeId)),
    )
    .sort((a, b) => a.position - b.position || a.slug.localeCompare(b.slug));
  return groups.map((g) => ({
    id: g.id,
    slug: g.slug,
    name: g.name,
    kind: fieldKind(g),
    required: g.requiredForTypeIds.includes(typeId),
    labels: schema.labels
      .filter((l) => l.groupId === g.id && !l.archivedAt && scopeApplies(l, product))
      .sort((a, b) => a.position - b.position || a.slug.localeCompare(b.slug))
      .map((l) => ({ id: l.id, slug: l.slug, name: l.name, color: l.color, position: l.position })),
  }));
}

/** Only single-select Fields can be grouped by (board columns, `sort:phase`). */
export function canGroupBy(group: Pick<FieldGroupDef, "exclusive"> | Pick<Field, "kind">): boolean {
  return "kind" in group ? group.kind === "single" : group.exclusive;
}

/**
 * The item's label ids per Field, keyed by group slug. Only groups the item has a value for are
 * present; use `values[slug] ?? []`. Ungrouped labels (plain tags) are not included.
 */
export function fieldValues(
  item: { labelIds: readonly string[] },
  schema: Pick<FieldsSchema, "labelGroups" | "labels">,
): Record<string, string[]> {
  const groupOf = new Map(schema.labels.map((l) => [l.id, l.groupId]));
  const slugOf = new Map(schema.labelGroups.map((g) => [g.id, g.slug]));
  const out: Record<string, string[]> = {};
  for (const id of item.labelIds) {
    const groupId = groupOf.get(id);
    const slug = groupId ? slugOf.get(groupId) : undefined;
    if (!slug) continue;
    const list = out[slug] ?? [];
    list.push(id);
    out[slug] = list;
  }
  return out;
}
